/**
 * Typed accessors over `notifications.en.ts`.
 *
 * Components never index the string map directly — they ask for a key (typo
 * = compile error) or for the copy that matches a *state*, so the mapping from
 * permission / connection state to wording lives in one place and the page
 * stays a thin template.
 */
import { notificationsEn, type NotificationCopyKey } from '@/i18n/notifications.en';
import type {
  DeviceNotificationState,
  DeviceNotificationStatus,
} from '@/services/device/notifications.service';
import type { RealtimeStatus, RealtimeSyncState } from '@/stores/realtime.store';

/** Look up one string by its catalogue key. */
export function notificationCopy(key: NotificationCopyKey): string {
  return notificationsEn[key];
}

/**
 * Full-sentence explanation of the rendered device-notification state —
 * ERPat's six (phase6-contract.md §3.5.2; LANG-027..030, 072).
 */
export function deviceStateCopy(state: DeviceNotificationState): string {
  switch (state) {
    case 'granted':
      return notificationsEn['notifications.device.enabled'];
    case 'disabled':
      return notificationsEn['notifications.device.disabled'];
    case 'invalid':
      return notificationsEn['notifications.device.invalid'];
    case 'denied':
      return notificationsEn['notifications.device.denied'];
    case 'unsupported':
      return notificationsEn['notifications.device.unsupported'];
    case 'default':
      return notificationsEn['notifications.device.default'];
  }
}

/** Short chip label for the rendered device-notification state. */
export function deviceStateLabel(state: DeviceNotificationState): string {
  switch (state) {
    case 'granted':
      return notificationsEn['notifications.settings.granted'];
    case 'disabled':
      return notificationsEn['notifications.settings.disabled'];
    case 'invalid':
      return notificationsEn['notifications.settings.invalid'];
    case 'denied':
      return notificationsEn['notifications.settings.denied'];
    case 'unsupported':
      return notificationsEn['notifications.settings.unsupported'];
    case 'default':
      return notificationsEn['notifications.settings.undecided'];
  }
}

/**
 * The background-delivery line under the state (the `push` + `policy` axes):
 * shown only while the state is `granted` or `invalid`; `null` otherwise.
 * `pending` is the moment between Enable and the server's answer.
 */
export function deviceBackgroundCopy(status: DeviceNotificationStatus, pending = false): string | null {
  if (status.state === 'invalid') return notificationsEn['notifications.device.backgroundInvalid'];
  if (status.state !== 'granted') return null;
  if (status.push === 'active') return notificationsEn['notifications.device.backgroundOn'];
  if (status.policy === 'available' && status.push === 'off') {
    return pending ? notificationsEn['notifications.device.backgroundPending'] : null;
  }
  return notificationsEn['notifications.device.backgroundUnavailable'];
}

/**
 * One line describing the live-updates connection. `enabled` is the server's
 * verdict from bootstrap: when it is false the socket never opens, and the
 * honest message is "not enabled for your company", not "offline".
 */
export function realtimeStatusCopy(status: RealtimeStatus, enabled: boolean): string {
  // A failed bootstrap leaves `enabled` false (the server never answered) while
  // the service reports `unavailable` and retries — that is a reconnect, not a
  // "not enabled for your company" verdict, so check it before the enabled gate.
  if (status === 'unavailable') {
    return notificationsEn['notifications.realtime.reconnecting'];
  }
  if (!enabled || status === 'disabled') {
    return notificationsEn['notifications.settings.realtimeDisabled'];
  }
  switch (status) {
    case 'connected':
      return notificationsEn['notifications.realtime.connected'];
    case 'connecting':
      return notificationsEn['notifications.settings.realtimeConnecting'];
    case 'failed':
      return notificationsEn['notifications.realtime.offline'];
    case 'disconnected':
    case 'idle':
      return notificationsEn['notifications.settings.realtimeDisconnected'];
  }
}

/**
 * One explanatory line for the realtime store's `lastError` reason class, or
 * `null` when the class needs no extra wording (`version_mismatch` is already
 * covered by the disabled status copy; unknown classes stay silent). This is
 * what tells an origin rejection apart from a backend outage or an auth refusal
 * — three failures that otherwise all read "Reconnecting…".
 */
export function realtimeErrorCopy(code: string | null): string | null {
  switch (code) {
    case 'origin_mismatch':
      return notificationsEn['notifications.realtime.errorOrigin'];
    case 'connection_error':
      // pusher-js's generic transport/protocol error: a dropped socket, a gateway
      // restart, a `pusher:error` frame. Deliberately NOT the origin wording —
      // an origin refusal never reaches this event (see realtime.service.ts).
      return notificationsEn['notifications.realtime.errorConnection'];
    case 'bootstrap_failed':
      return notificationsEn['notifications.realtime.errorBootstrap'];
    case 'subscription_error':
    case 'channel_auth_failed':
      return notificationsEn['notifications.realtime.errorAuth'];
    case 'connection_refused':
      // A 4001/4007/4009 close: the gateway ended the session on purpose
      // (logout elsewhere, password/role change, tenant switch). The client
      // re-bootstraps; if the session really is gone the API's 401 signs out.
      return notificationsEn['notifications.realtime.errorRefused'];
    case 'gateway_loopback':
      // DEC-102, the hard verdict: the bootstrap named a loopback gateway while
      // the API is remote (a dev `.env` copied to production). The service
      // does not dial — every device would connect to itself — and polls.
      return notificationsEn['notifications.realtime.errorGatewayLoopback'];
    case 'gateway_unexpected':
      // DEC-102, advisory: the bootstrap's gateway is not the pinned one
      // (`VITE_REALTIME_GATEWAY_ORIGINS`). The socket is still dialled; a
      // successful connect clears this line, so it only shows while it matters.
      return notificationsEn['notifications.realtime.errorGatewayUnexpected'];
    default:
      return null;
  }
}

/**
 * One short line for the catch-up state — whether the local inbox is known to
 * be complete — shown beside the connection status. `null` while realtime is
 * not enabled at all (the disabled copy already says everything).
 */
export function realtimeSyncCopy(state: RealtimeSyncState, enabled: boolean): string | null {
  if (!enabled) return null;
  switch (state) {
    case 'idle':
      return notificationsEn['notifications.realtime.syncIdle'];
    case 'syncing':
      return notificationsEn['notifications.realtime.syncing'];
    case 'synchronized':
      return notificationsEn['notifications.realtime.synchronized'];
    case 'degraded':
      return notificationsEn['notifications.realtime.degraded'];
  }
}

/** Title/body used for message events, which are always content-free on the wire. */
export function messageAlertCopy(): { title: string; body: string } {
  return {
    title: notificationsEn['notifications.message.title'],
    body: notificationsEn['notifications.message.body'],
  };
}

/** Title/body for a burst of `count` notifications that arrived together. */
export function burstCopy(count: number): { title: string; body: string } {
  return {
    title: notificationsEn['notifications.burst.title'],
    body: notificationsEn['notifications.burst.body'].replace('{count}', String(count)),
  };
}

/** Title/body used when an envelope carries no renderable notification text. */
export function privateFallbackCopy(): { title: string; body: string } {
  return {
    title: notificationsEn['notifications.private.title'],
    body: notificationsEn['notifications.private.body'],
  };
}
